import { useMemo } from "react";

import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { TutorialVideoCard } from "src/components/Analytics/TutorialVideoCard";
import { DataChart } from "src/components/dataView/DataChart/DataChart";
import { DataExportButton } from "src/components/dataView/DataExport/DataExportButton";
import { DataTable } from "src/components/dataView/DataTable/DataTable";
import DataTableViewSettings from "src/components/dataView/DataTable/DataTableViewSettings";
import {
  colorGradient,
  getExcelExportData,
} from "src/components/dataView/DataTable/tableHelpers";
import { MetricsTabSwitcherFilter } from "src/components/dataView/MetricsTable/MetricsTabSwitcherFilter";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import { tutorialVideos } from "src/lib/constants";
import { numberFormat as defaultNumberFormat } from "src/lib/format";
import { cohortAtom, useAnalytics } from "src/lib/hooks/useAnalytics";
import { COHORT_ANALYTICS_QUERY } from "src/lib/queries/analytics";

import { CohortMetricGuide } from "./CohortMetricGuide";

const ltvToCacDomain = ({ table }) => {
  const maxValue = Math.max(
    ...table
      .getRowModel()
      .rows.map((r) => (r.original.ltv30 != null ? r.original.ltv30 : 0))
  );

  return [0, 1, Math.max(3, maxValue)];
};

const getMetricsColumns = (numberFormat = defaultNumberFormat) => [
  {
    title: "Customers",
    field: "count",
    viewByDefault: true,
    format: numberFormat.whole,
  },
  {
    title: "nCAC",
    field: "nCAC",
    viewByDefault: true,
    format: numberFormat.currency,
  },
  {
    title: "Repeat Customer Rate",
    field: "repeatCustomerRate",
    viewByDefault: true,
    format: numberFormat.percent,
  },
  // { title: "AOF", field: "aof" },
  // { title: "1st NS", field: "firstNS" },
  // { title: "Refund %", field: "refund" },
  // { title: "Churn %", field: "churn" },
  // { title: "30-Day Repeat, %", field: "repeat30" },
  // { title: "Repeat Days, #", field: "repeatDays" },
  // { title: "30-Day Threpeat, %", field: "threpeat30" },
  {
    title: "30-Day LTV:nCAC",
    field: "ltv30",
    inMetrics: true,
    viewByDefault: true,
    format: numberFormat.normal,
    color: ["var(--chart-bad)", "var(--chart-middle)", "var(--chart-good)"],
    domain: ltvToCacDomain,
  },
  {
    title: "90-Day LTV:nCAC",
    field: "ltv90",
    inMetrics: true,
    viewByDefault: true,
    format: numberFormat.normal,
    color: ["var(--chart-bad)", "var(--chart-middle)", "var(--chart-good)"],
    domain: ltvToCacDomain,
  },
  {
    title: "nCAC Payback",
    field: "cacPayback",
    inMetrics: true,
    viewByDefault: true,
    format: (value) => {
      return value === "CAC Not Repaid"
        ? value
        : "Month " + numberFormat.whole(value);
    },
    color: ({ row }) => {
      const value = row.original.cacPayback;
      if (value === "CAC Not Repaid") {
        return "var(--chart-bad)";
      }

      return ["var(--chart-good)", "var(--chart-3)"];
    },
    domain: ({ row }) => {
      const value = row.original.cacPayback;
      if (value === "CAC Not Repaid") {
        return [0, 0];
      }

      return [0, Math.max(3, value)];
    },
  },
];

const persistentCohortAtom = atomWithStorage("persistentCohortAtom", {
  viewOptions: getMetricsColumns().map((column) => ({
    name: column.title,
    value: column.viewByDefault,
  })),
});

const formatPeriod = (p) =>
  new Date(p + "-01T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

const excelStyling = [
  {
    rows: [1],
    bold: true,
    border: "bottom",
  },
  {
    rows: [14],
    bold: true,
    cellColor: "E3DFEB",
    border: "top",
  },
  {
    columns: ["G"],
    border: "right",
  },
];

const CohortAnalytics = () => {
  const [persistentStore, setPersistentStore] = useAtom(persistentCohortAtom);

  const analytics = useAnalytics(
    cohortAtom,
    COHORT_ANALYTICS_QUERY,
    "cohortAnalytics",
    ({ data }) => [...data].sort((a, b) => a.period.localeCompare(b.period))
  );
  const { store, refreshData, numberFormat } = analytics;
  const metricsColumns = useMemo(
    () => getMetricsColumns(numberFormat),
    [numberFormat]
  );

  const metricType = store.metricType || "profitContributionMetrics";
  const isContribution = metricType === "profitContributionMetrics";
  const sourceMetricType = isContribution ? "profitMetrics" : metricType;
  const metricLabel = isContribution
    ? "Profit Contribution"
    : metricType === "revenueMetrics"
      ? "Net Revenue"
      : "Gross Profit";
  const showTotals = store.displayMode === "total";
  const displayModeLabel = showTotals ? "Cohort totals" : "Per customer";
  const visibleMetrics = useMemo(
    () =>
      metricsColumns.filter(
        (m) =>
          persistentStore.viewOptions.find((o) => o.name === m.title)?.value
      ),
    [persistentStore.viewOptions, metricsColumns]
  );

  // Derive cohort offsets from the longest metrics[] series (0, 30, 60, …)
  const offsets = useMemo(() => {
    const len = Math.max(
      ...(store.data || []).map(
        (d) => d[sourceMetricType].metrics?.length || 0
      ),
      0
    );
    return Array.from({ length: len }, (_, i) => i * 30);
  }, [sourceMetricType, store.data]);

  // Rows: one per cohort period, plus Average row at bottom
  const rows = useMemo(() => {
    const base = (store.data || []).map((datum) => {
      // Acquisition is deducted once from each cumulative period value.
      const perCustomerMetrics = (datum[sourceMetricType].metrics || []).map(
        (value) =>
          isContribution && value != null
            ? datum.nCAC == null
              ? null
              : value - datum.nCAC
            : value
      );
      const row = {
        id: datum.period,
        period: formatPeriod(datum.period),
        // Colors describe per-customer performance regardless of display mode
        // or which summary columns are visible.
        meta: {
          breakEven: isContribution ? 0 : datum.nCAC,
          perCustomerMetrics,
        },
      };
      visibleMetrics.forEach(({ field, inMetrics }) => {
        let value;
        if (inMetrics) {
          value = datum[sourceMetricType]?.[field];
        } else {
          value = datum[field];
        }

        if (showTotals && field === "nCAC" && value != null) {
          value *= datum.count;
        }

        return (row[field] = value);
      });
      offsets.forEach((_, idx) => {
        const value = perCustomerMetrics[idx];
        row[`m${idx}`] =
          showTotals && value != null ? value * datum.count : value;
      });
      return row;
    });

    // Average row over visible metrics & cohort offsets
    if (base.length) {
      const breakEvenValues = base
        .map((row) => row.meta.breakEven)
        .filter((value) => value != null && Number.isFinite(value));
      const avg = {
        id: "Average",
        period: "Average",
        meta: {
          breakEven: breakEvenValues.length
            ? breakEvenValues.reduce((sum, value) => sum + value, 0) /
              breakEvenValues.length
            : null,
          perCustomerMetrics: [],
          highlight: true,
          className: "font-semibold border-t border-t-muted-foreground",
        },
      };

      // Calculate average for each visible metric
      visibleMetrics.forEach(({ field, title }) => {
        const rawValues = base.map((row) => row[field]);

        if (field === "cacPayback" || /payback/i.test(title || field)) {
          // Special rule for Payback:
          // 1) If > 50% are "CAC Not Repaid" → return that
          // 2) Else average numeric months and round to whole number
          const notRepaid = rawValues.filter(
            (value) => value === "CAC Not Repaid"
          ).length;
          const total = rawValues.length;

          if (notRepaid > total / 2) {
            avg[field] = "CAC Not Repaid";
          } else {
            const nums = rawValues
              .filter((value) => value !== "CAC Not Repaid" && value != null)
              .map((value) =>
                typeof value === "string" && /^month/i.test(value)
                  ? parseFloat(value.replace(/month/i, "").trim())
                  : Number(value)
              )
              .filter((number) => Number.isFinite(number));

            if (nums.length) {
              const sum = nums.reduce((s, n) => s + n, 0);
              avg[field] = Math.round(sum / nums.length);
            } else {
              avg[field] = null; // blank cell
            }
          }
        } else {
          const values = rawValues
            .filter((value) => value !== undefined && value !== null)
            .map((value) => Number(value))
            .filter((number) => Number.isFinite(number));

          const sum = values.reduce((acc, number) => acc + number, 0);
          avg[field] = values.length ? sum / values.length : null; // null renders blank
        }
      });

      // Calculate average for each cohort offset
      offsets.forEach((_, idx) => {
        const values = base
          .map((row) => row[`m${idx}`])
          .filter((value) => value !== undefined && value !== null)
          .map((value) => Number(value))
          .filter((number) => Number.isFinite(number));

        const sum = values.reduce((acc, number) => acc + number, 0);
        avg[`m${idx}`] = values.length ? sum / values.length : null;

        const perCustomerValues = base
          .map((row) => row.meta.perCustomerMetrics[idx])
          .filter((value) => value != null)
          .map(Number)
          .filter(Number.isFinite);
        avg.meta.perCustomerMetrics[idx] = perCustomerValues.length
          ? perCustomerValues.reduce((total, value) => total + value, 0) /
            perCustomerValues.length
          : null;
      });

      return [...base, avg];
    }

    return base;
  }, [
    store.data,
    visibleMetrics,
    offsets,
    sourceMetricType,
    isContribution,
    showTotals,
  ]);

  const topValuesByField = useMemo(() => {
    if (!rows?.length) return {};

    const targetFields = ["count", "nCAC", "repeatCustomerRate"];
    const result = {};

    targetFields.forEach((field) => {
      const sorted = rows
        .filter((row) => row.id !== "Average" && row.period !== "Average")
        .map((r) => r[field])
        .filter((v) => v != null && Number.isFinite(Number(v)))
        .sort((a, b) => Number(b) - Number(a));
      result[field] = new Set(
        sorted.slice(0, Math.min(3, Math.max(0, sorted.length - 2)))
      );
    });

    return result;
  }, [rows]);

  const columns = useMemo(() => {
    const columns = [
      {
        header: "Period",
        accessorKey: "period",
        meta: {
          sticky: true,
          className: "min-w-[120px] text-left",
          rightBorder: true,
        },
      },
    ];

    if (visibleMetrics.length) {
      columns.push({
        id: "summary",
        header: "Summary",
        columns: visibleMetrics.map((m) => {
          let cell;
          if (m.color) {
            const baseCell = colorGradient({
              colors: m.color,
              asBadge: true,
              minOpacity: 0.2,
              format: m.format || numberFormat.currency,
              domain: m.domain || undefined,
              coerceNonFinite: false,
              returnOnNonFinite: false,
            });

            // Hide 0s for 90-Day LTV:nCAC
            if (m.field === "ltv90") {
              cell = (ctx) => {
                if (Number(ctx.getValue?.()) === 0) {
                  return null;
                }
                return baseCell(ctx);
              };
            } else {
              cell = baseCell;
            }
          } else {
            cell = ({ getValue }) => {
              const value = getValue();
              const formatted = (m.format || numberFormat.currency)(value);

              if (topValuesByField[m.field]?.has(value)) {
                return (
                  <span className="group-data-[loading=false]:text-primary font-semibold">
                    {formatted}
                  </span>
                );
              }

              return formatted;
            };
          }

          return {
            id: m.field,
            header:
              showTotals && m.field === "nCAC" ? "Acquisition cost" : m.title,
            accessorKey: m.field,
            meta: { className: "min-w-[110px] text-center", format: m.format },
            cell,
          };
        }),
      });
    }

    if (offsets.length) {
      columns.push({
        id: "cohort",
        header: `${metricLabel} · ${displayModeLabel}`,
        columns: offsets.map((off, idx) => {
          return {
            id: `m${idx}`,
            header: `${off}`,
            accessorKey: `m${idx}`,
            meta: {
              className:
                "min-w-[90px] group-data-[loading=false]:p-0 group-data-[loading=false]:border-b-0 bg-background",
              leftBorder: idx === 0,
            },
            cell: (context) =>
              colorGradient({
                colors: [
                  "var(--chart-bad)",
                  "transparent",
                  "var(--chart-good)",
                ],
                format: () => numberFormat.currency(context.getValue()),
                coerceNonFinite: false,
                domain: ({ row, table }) => {
                  const { breakEven } = row.original.meta || {};

                  const allValues = table
                    .getRowModel()
                    .rows.flatMap(
                      (row) => row.original.meta?.perCustomerMetrics || []
                    )
                    .filter((value) => value != null)
                    .map(Number)
                    .filter(Number.isFinite);
                  const globalMin = Math.min(...allValues);
                  const globalMax = Math.max(...allValues);

                  return [globalMin, breakEven, globalMax];
                },
              })({
                ...context,
                getValue: () =>
                  context.row.original.meta?.perCustomerMetrics[idx],
              }),
          };
        }),
      });
    }

    return columns;
  }, [
    visibleMetrics,
    offsets,
    topValuesByField,
    numberFormat,
    metricLabel,
    displayModeLabel,
    showTotals,
  ]);

  const tableHeaders = useMemo(() => {
    const flat = [];
    columns.forEach((col) => {
      if (col.columns) {
        col.columns.forEach((sub) =>
          flat.push({
            header: sub.header,
            accessorKey: sub.accessorKey,
            // Carry through format (if any) for Excel export
            format: sub.meta?.format,
          })
        );
      } else {
        flat.push({ header: col.header, accessorKey: col.accessorKey });
      }
    });
    return flat;
  }, [columns]);

  const exportTableData = useMemo(() => {
    return getExcelExportData(
      rows,
      tableHeaders,
      "period",
      numberFormat.currency
    );
  }, [rows, tableHeaders, numberFormat]);

  // Loading skeleton that preserves shape
  const loadingData = useMemo(() => {
    const length = Math.max(6, store.data?.length || 0);

    return Array.from({ length }, (_, i) => {
      const row = { id: `loading-${i}`, period: " " };

      visibleMetrics.forEach(({ field }) => (row[field] = undefined));
      offsets.forEach((_, idx) => (row[`m${idx}`] = undefined));

      return row;
    });
  }, [store.data?.length, visibleMetrics, offsets]);

  const paybackChartPrep = useMemo(() => {
    const rows = store.data || [];
    const numeric = rows
      .map((d) =>
        d[sourceMetricType].cacPayback === "CAC Not Repaid"
          ? null
          : Number(d[sourceMetricType].cacPayback)
      )
      .filter(Number.isFinite);

    const maxPaid = numeric.length ? Math.max(...numeric) : 0;

    const data = rows.map((d) => {
      const isNR = d[sourceMetricType].cacPayback === "CAC Not Repaid";
      return {
        period: formatPeriod(d.period),
        payback: isNR ? maxPaid * 2 : Number(d[sourceMetricType].cacPayback),
        nCAC: showTotals && d.nCAC != null ? d.nCAC * d.count : d.nCAC,
        customers: d.count,
        meta: isNR ? { hatch: true, color: "var(--chart-bad)" } : {},
      };
    });

    return { data, maxPaid };
  }, [sourceMetricType, store.data, showTotals]);

  const paybackChartConfig = {
    payback: {
      label: "Payback (months)",
      type: "bar",
      color: "var(--chart-2)",
      yAxis: {
        id: "left",
        width: 30,
        domain: [0, paybackChartPrep.maxPaid + 1],
        tickFormatter: (value) => numberFormat.whole(value),
      },
    },
    nCAC: {
      label: `${showTotals ? "Acquisition cost" : "nCAC"} (${numberFormat.currencyCode})`,
      color: "var(--chart-1)",
      type: "line",
      toggleable: true,
      defaultToggled: false,
      yAxis: {
        id: "right",
        orientation: "right",
        tickFormatter: (value) => numberFormat.currency(value),
      },
    },
  };

  return (
    <>
      <DashboardLayout.Header onRefresh={refreshData} loading={store.loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters>
          <DateRangePicker atom={cohortAtom} onSubmit={refreshData} />
          <MetricsTabSwitcherFilter
            tooltip={
              <div className="space-y-2">
                <p>
                  <strong>Profit Contribution</strong>
                  <br />
                  Gross profit per customer − nCAC.
                </p>
                <p>
                  <strong>Gross Profit</strong>
                  <br />
                  Before marketing costs.
                </p>
              </div>
            }
            atom={cohortAtom}
            field="metricType"
            defaultValue="profitContributionMetrics"
            options={[
              {
                title: "Profit Contribution",
                value: "profitContributionMetrics",
              },
              { title: "Gross Profit", value: "profitMetrics" },
              { title: "Net Revenue", value: "revenueMetrics" },
            ]}
            queryVariableMapping={null} // don't store it in queryVariables, we only change this on the frontend
            disabled={store.loading}
          />
          <MetricsTabSwitcherFilter
            tooltip="Show amounts per customer or for the whole cohort."
            atom={cohortAtom}
            field="displayMode"
            defaultValue="perCustomer"
            options={[
              { title: "Per customer", value: "perCustomer" },
              { title: "Cohort totals", value: "total" },
            ]}
            queryVariableMapping={null}
            disabled={store.loading}
          />
          <div className="ml-auto">
            <DataExportButton
              disabled={store.loading}
              title={`Cohort Analytics (${metricLabel}, ${displayModeLabel})`}
              tableData={exportTableData}
              tableHeaders={tableHeaders}
              styles={excelStyling}
            />
          </div>
        </DashboardLayout.Filters>

        <DashboardLayout.Cards className="relative">
          <DataTable
            columns={columns}
            data={rows}
            atom={cohortAtom}
            className="max-w-fit"
            loadingData={loadingData}
            alternateRowColors
          />
          <div className="absolute top-1 left-1 z-20">
            <DataTableViewSettings
              options={persistentStore.viewOptions.map((option) => ({
                ...option,
                name:
                  showTotals && option.name === "nCAC"
                    ? "Acquisition cost"
                    : option.name,
              }))}
              onOptionChange={(option, value) => {
                const name =
                  option.name === "Acquisition cost" ? "nCAC" : option.name;
                setPersistentStore((prev) => {
                  const viewOptions = prev.viewOptions.map((o) =>
                    o.name === name ? { ...o, value } : o
                  );
                  return { ...prev, viewOptions };
                });
              }}
              onReset={() => {
                setPersistentStore((prev) => ({
                  ...prev,
                  viewOptions: metricsColumns.map((col) => ({
                    name: col.title,
                    value: col.viewByDefault,
                  })),
                }));
              }}
              isColumns
            />
          </div>
        </DashboardLayout.Cards>

        <DashboardLayout.Cards className="mt-4">
          <CohortMetricGuide numberFormat={numberFormat} />
        </DashboardLayout.Cards>

        <DashboardLayout.Cards className="flex flex-col-reverse gap-8 @lg/main:flex-row">
          <DataChart
            data={paybackChartPrep.data}
            loading={store.loading}
            dateFrom={store.dateFrom}
            dateTo={store.dateTo}
            xAxisDataKey="period"
            className="w-full"
            chartConfig={paybackChartConfig}
            title="Cohort nCAC Payback Timeline"
            description="Months to repay nCAC; NR = not repaid within observed period."
            showYAxis
            valueFormatter={(value) => numberFormat.currency(value)}
            tooltipValueFormatter={(value, name, item) => {
              if (name === "payback") {
                const row = item?.payload;

                if (row?.meta?.hatch) {
                  return "Not repaid";
                }
                if (value == null) {
                  return "-";
                }

                return numberFormat.whole(value);
              }

              return numberFormat.currency(value);
            }}
          />

          <TutorialVideoCard {...tutorialVideos.cohorts} />
        </DashboardLayout.Cards>
      </DashboardLayout.Main>
    </>
  );
};

export default CohortAnalytics;
